import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FaPlus,
  FaEye,
  FaEdit,
  FaTrash,
  FaTimes,
  FaSearch,
  FaTruck,
  FaMapMarkerAlt,
  FaEnvelope,
  FaPhoneAlt,
  FaHome,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

const DistributorPage = () => {
  const navigate = useNavigate();
  const [distributorList, setDistributorList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [globalFilter, setGlobalFilter] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    page: 1,
    per_page: 5,
    search: "",
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [selectedDistributor, setSelectedDistributor] = useState(null);

  // ======================= FETCH DATA ==========================
  const fetchDistributor = async (filters = {}) => {
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!token) {
      Swal.fire("Error", "Token tidak ditemukan, silakan login ulang.", "error");
      setLoading(false);
      return;
    }

    const params = new URLSearchParams({
      per_page: filters.per_page || appliedFilters.per_page,
      page: filters.page || appliedFilters.page,
      search: filters.search || globalFilter,
    }).toString();

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/distributor?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Gagal mengambil data distributor");
      }

      const data = await response.json();
      setDistributorList(data.data);
      setTotalPages(data.meta?.last_page || 1);
      setTotalData(data.meta?.total || 0);
    } catch (error) {
      Swal.fire("Gagal", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDistributor();
  }, [appliedFilters, globalFilter]);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleCreate = () => {
    navigate("/enterprise/distributor/create-distributor");
  };

  const handleView = (distributor) => {
    setSelectedDistributor(distributor);
  };

  const handleEdit = (id) => {
    navigate(`/enterprise/distributor/edit-distributor/${id}`);
  };

  // ======================= DELETE WITH SWEETALERT ==========================
  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: "Yakin ingin menghapus?",
      text: `Distributor "${name}" akan dihapus permanen.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus!",
      cancelButtonText: "Batal",
      confirmButtonColor: "#e3342f",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem("token");

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/v1/distributor/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "Gagal menghapus distributor");
      }

      setDistributorList((prev) => prev.filter((d) => d.id !== id));

      Swal.fire("Berhasil!", `Distributor "${name}" telah dihapus.`, "success");
    } catch (error) {
      Swal.fire("Gagal", error.message, "error");
    }
  };

  const handleCloseDetail = () => {
    setSelectedDistributor(null);
  };

  return (
    <div className="p-5 min-h-screen flex flex-col space-y-6">
      {/* ================= HEADER PANEL ================= */}
      <div className="bg-white shadow-lg rounded-2xl p-5 border border-gray-100">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-green-100 rounded-2xl shadow-inner">
            <FaTruck className="text-green-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-800">Distributor</h1>
            <p className="text-gray-500 text-sm">
              Menampilkan semua data Distributor yang terdaftar
            </p>
          </div>
        </div>

        {/* BREADCRUMB */}
        <nav aria-label="Breadcrumb" className="mt-3 text-gray-500 text-sm">
          <ol className="flex items-center space-x-2">
            <li>
              <button
                onClick={() => navigate("/")}
                className="hover:text-gray-700 transition"
              >
                Enterprise
              </button>
            </li>
            <li>/</li>
            <li className="text-gray-400">Distributor</li>
          </ol>
        </nav>
      </div>

      {/* ================= WRAPPER ================= */}
      <div className="flex w-full gap-4">
        {/* ================= MAIN TABLE PANEL ================= */}
        <div
          className={`transition-all duration-300 ${
            selectedDistributor ? "md:w-2/3" : "w-full"
          }`}
        >
          <div className="bg-white rounded-2xl shadow-md p-6">
            {/* CONTROL PANEL */}
            <div className="flex flex-wrap justify-between items-center mb-5 gap-3">
              <button
                onClick={handleCreate}
                className="flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-white font-medium px-4 py-2 rounded-xl shadow-md transition"
              >
                <FaPlus size={16} /> Tambah Distributor
              </button>

              {/* SEARCH */}
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <div className="relative w-full sm:w-64">
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Cari distributor..."
                    value={globalFilter}
                    onChange={(e) => setGlobalFilter(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
              </div>
            </div>

            {/* ================= TABLE ================= */}
            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
              
              {loading ? (
                <p className="text-center text-gray-600 py-10">Loading...</p>
              ) : (
                <table className="min-w-full bg-white rounded-2xl">
                <thead className="bg-green-50">
                    <tr>
                      <th className="p-4 text-left font-semibold text-gray-700 border-b">No</th>
                      <th className="p-4 text-left font-semibold text-gray-700 border-b">Nama</th>
                      <th className="p-4 text-left font-semibold text-gray-700 border-b">Kota</th>
                      <th className="p-4 text-left font-semibold text-gray-700 border-b">Telepon</th>
                      <th className="p-4 text-left font-semibold text-gray-700 border-b text-center">Aksi</th>
                    </tr>
                  </thead>

                  <tbody>
                    {distributorList.length > 0 ? (
                      distributorList.map((distributor, index) => (
                        <tr
                          key={distributor.id}
                          className="hover:bg-gray-50 border-t border-gray-200"
                        >
                          <td className="px-4 py-2 border-b">{index + 1}</td>
                          <td className="px-4 py-2 border-b">
                            {distributor.name}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {distributor.kota}
                          </td>
                          <td className="px-4 py-2 border-b">
                            {distributor.telepon}
                          </td>

                          <td className="px-4 py-2 border-b">
                            <div className="flex justify-center gap-2">
                              {/* DETAIL */}
                              <button
                                onClick={() => handleView(distributor)}
                                className="p-2 bg-green-100 hover:bg-green-200 rounded-lg shadow-md text-green-600 hover:text-green-700 transition transform hover:scale-110"
                                title="Lihat"
                              >
                                <FaEye size={16} />
                              </button>

                              {/* EDIT */}
                              <button
                                onClick={() => handleEdit(distributor.id)}
                                className="p-2 bg-blue-100 hover:bg-blue-200 rounded-lg shadow-md text-blue-600 hover:text-blue-700 transition transform hover:scale-110"
                                title="Edit"
                              >
                                <FaEdit size={16} />
                              </button>

                              {/* DELETE */}
                              <button
                                onClick={() =>
                                  handleDelete(distributor.id, distributor.name)
                                }
                                className="p-2 bg-red-100 hover:bg-red-200 rounded-lg shadow-md text-red-600 hover:text-red-700 transition transform hover:scale-110"
                                title="Hapus"
                              >
                                <FaTrash size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center py-6 text-gray-500">
                          Tidak ada data distributor
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

            {/* ================= PAGINATION ================= */}
            <div className="flex justify-between items-center mt-6 flex-wrap gap-3">
              <p className="text-sm text-gray-500">
                Menampilkan {distributorList.length} dari total {totalData} distributor
              </p>

              <div className="flex items-center space-x-2">
                <button
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 hover:bg-green-200 text-gray-700 rounded-xl transition disabled:opacity-40"
                  onClick={() => handlePageChange(appliedFilters.page - 1)}
                  disabled={appliedFilters.page <= 1}
                >
                  <FiChevronLeft /> Sebelumnya
                </button>

                <span className="text-sm text-gray-600">
                  Halaman <strong>{appliedFilters.page}</strong> dari{" "}
                  <strong>{totalPages}</strong>
                </span>

                <button
                  className="flex items-center gap-1 px-3 py-1 text-sm bg-green-100 hover:bg-green-200 text-gray-700 rounded-xl transition disabled:opacity-40"
                  onClick={() => handlePageChange(appliedFilters.page + 1)}
                  disabled={appliedFilters.page >= totalPages}
                >
                  Berikutnya <FiChevronRight />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ================= DETAIL PANEL ================= */}
        {selectedDistributor && (
          <div
            className="w-full md:w-1/3 bg-white rounded-2xl shadow-xl p-6 relative z-40 animate-slideLeft"
          >
            {/* Close button */}
            <button
              aria-label="Close detail"
              onClick={handleCloseDetail}
              className="absolute top-4 right-4 p-2.5 bg-white rounded-full shadow-sm border border-gray-100 
                text-gray-600 hover:bg-red-50 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-300 
                transition z-50"
              title="Tutup"
            >
              <FaTimes size={18} />
            </button>

            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-yellow-100 text-yellow-500 rounded-full p-3">
                <FaTruck size={20} />
              </div>
              <h2 className="text-xl font-semibold text-gray-800">
                {selectedDistributor.name}
              </h2>
            </div>

            <div className="flex flex-col space-y-4 text-sm text-gray-700">
              <div className="flex items-start space-x-2">
                <FaMapMarkerAlt className="text-blue-500 mt-1" />
                <p>
                  <strong>Kota:</strong> {selectedDistributor.kota}
                </p>
              </div>

              <div className="flex items-start space-x-2">
                <FaEnvelope className="text-green-500 mt-1" />
                <p>
                  <strong>Email:</strong>{" "}
                  {selectedDistributor.email || "-"}
                </p>
              </div>

              <div className="flex items-start space-x-2">
                <FaPhoneAlt className="text-purple-500 mt-1" />
                <p>
                  <strong>No. HP:</strong> {selectedDistributor.telepon}
                </p>
              </div>

              <div className="flex items-start space-x-2">
                <FaHome className="text-pink-500 mt-1" />
                <p>
                  <strong>Alamat:</strong>{" "}
                  {selectedDistributor.alamat || "-"}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DistributorPage;
